/**
 * The instant an appointment happens, and how to spell it.
 *
 * Twin of `blhClinicApp/src/services/schedule/appointmentClock.ts`, written
 * against `Intl` because this app is on date-fns 2 and carries no `@date-fns/tz`.
 * Keep the two in step: same names, same contracts.
 *
 * Why it exists here: every date this app showed or exported was rebuilt by
 * parsing the legacy `date` / `time` strings — trying "MM/DD/YYYY" first (a
 * format we do not store), then `new Date("Monday, September 14, 2026")`, which
 * yields midnight in the viewer's zone and throws the appointment's real hour
 * away. `startAt` was never consulted, even though it is the exact instant and
 * always present: across all twelve tenants, 0 of 21,397 appointments lack it.
 *
 * `date` is a legacy mirror on its way out. Some rows carry an epoch in it, so
 * anything that reads it renders "1777498200000". Read `startAt`.
 */

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// An instant as stored (epoch ms), or null — never 1970.
function instantOf(value) {
  if (value === null || value === undefined || value === "") return null;
  const at = Number(value);
  return Number.isFinite(at) && at > 0 ? at : null;
}

/**
 * The appointment's start, in epoch milliseconds.
 *
 * A visit's time is `startAt` and `endAt`, nothing else (blhWebServer
 * docs/AVAILABILITY_MODEL_V2.md §2.6). `ISOdate` is NOT consulted — a recurring
 * instance keeps its series' FIRST week there — and neither are the `date` /
 * `time` strings, which cannot express a time of day reliably.
 *
 * @returns {number|null} epoch ms, or null when the row carries no start
 */
export function appointmentInstant(appointment) {
  return instantOf(appointment?.startAt);
}

/** The appointment's end, epoch ms, or null. */
export function appointmentEnd(appointment) {
  return instantOf(appointment?.endAt);
}

/**
 * The appointment's length in whole minutes: `endAt − startAt`. The
 * `duration` label is retired (it contradicted the span on 70 visits).
 * @returns {number|null}
 */
export function appointmentMinutes(appointment) {
  const start = appointmentInstant(appointment);
  const end = appointmentEnd(appointment);
  if (start === null || end === null || !(end > start)) return null;
  return Math.round((end - start) / 60000);
}

// How the clinic names a zone on screen: "AT" all year, never Intl's
// "AST"/"ADT" (blhClinicApp/src/components/timezoneLabels.js). An unmapped
// zone falls back to its city.
const ZONE_SHORT = Object.freeze({
  "America/St_Johns": "NT",
  "America/Halifax": "AT",
  "America/Toronto": "ET",
  "America/Winnipeg": "CT",
  "America/Edmonton": "MT",
  "America/Vancouver": "PT",
});

/** "America/Halifax" -> "AT"; "" without a zone. */
export function zoneLabel(timeZone) {
  if (!timeZone) return "";
  return ZONE_SHORT[timeZone] || timeZone.split("/").pop().replace(/_/g, " ");
}

/**
 * Fixed-offset abbreviations found on real location records, read as the zone
 * they were meant to name: "MST" does not follow daylight saving. The same
 * table as blhWebServer/src/domain/time/timeZone.js.
 */
const ABBREVIATION_TO_IANA = Object.freeze({
  MST: "America/Edmonton",
  MDT: "America/Edmonton",
  PST: "America/Vancouver",
  PDT: "America/Vancouver",
  EST: "America/Toronto",
  EDT: "America/Toronto",
  AST: "America/Halifax",
  ADT: "America/Halifax",
  CST: "America/Winnipeg",
  CDT: "America/Winnipeg",
  NST: "America/St_Johns",
  NDT: "America/St_Johns",
});

/** Whatever is stored, as a usable IANA zone, or null. */
export function normalizeTimeZone(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed === "UTC") return "UTC";
  if (trimmed.includes("/")) {
    try {
      new Intl.DateTimeFormat("en-US", {timeZone: trimmed});
      return trimmed;
    } catch {
      return null;
    }
  }
  return ABBREVIATION_TO_IANA[trimmed.toUpperCase()] || null;
}

/**
 * The zone an appointment's wall time is expressed in: its location's.
 *
 * The session bootstrap sends `locations` with an exclusion projection, so
 * `timeZone` is already on every location this app holds.
 *
 * @returns {string|null} an IANA zone name, or null for "use the viewer's"
 */
export function resolveAppointmentTimeZone({
  appointment = null,
  locations = null,
  location = null,
  organization = null,
} = {}) {
  const zoneOf = (holder) =>
    normalizeTimeZone(holder?.timeZone) || normalizeTimeZone(holder?.timezone);

  const fromLocation = zoneOf(location);
  if (fromLocation) return fromLocation;

  const lid = appointment?.lid || null;
  if (lid && Array.isArray(locations)) {
    const match = locations.find((l) => String(l?.lid) === String(lid));
    const zone = zoneOf(match);
    if (zone) return zone;
  }

  return zoneOf(organization);
}

// The calendar fields of an instant, read on a given clock.
function partsOn(timestamp, timeZone) {
  const at = Number(timestamp);
  if (!Number.isFinite(at) || at <= 0) return null;

  const formatter = new Intl.DateTimeFormat("en-US", {
    ...(timeZone ? {timeZone} : {}),
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });

  const parts = {};
  for (const part of formatter.formatToParts(new Date(at))) {
    parts[part.type] = part.value;
  }
  if (!WEEKDAYS.includes(parts.weekday)) return null;
  // `hour12: false` renders midnight as "24" in some ICU versions.
  const hour = Number(parts.hour) % 24;
  return {
    weekday: parts.weekday,
    month: parts.month,
    day: Number(parts.day),
    year: Number(parts.year),
    hour,
    minute: Number(parts.minute),
  };
}

/** "Monday, September 14, 2026" on the clinic's clock. */
export function legacyDateString(timestamp, timeZone = null) {
  const p = partsOn(timestamp, timeZone);
  if (!p) return "";
  return `${p.weekday}, ${p.month} ${p.day}, ${p.year}`;
}

/** "7:00 PM" on the clinic's clock. */
export function legacyTimeString(timestamp, timeZone = null) {
  const p = partsOn(timestamp, timeZone);
  if (!p) return "";
  const suffix = p.hour >= 12 ? "PM" : "AM";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${hour12}:${String(p.minute).padStart(2, "0")} ${suffix}`;
}

/** "7:00 PM ET": the time on the clinic's clock, the clock named. */
export function namedTimeString(timestamp, timeZone = null) {
  const time = legacyTimeString(timestamp, timeZone);
  const label = zoneLabel(timeZone);
  return time && label ? `${time} ${label}` : time;
}
